import type { LiveRoom } from './api';
import { fetchSiteBranding, getCurrentUser, navigate, showOneliveAlert } from './api';
import { buildLoginPath, isLoginPath, sanitizeLoginRedirect } from './authRedirect';

export type LiveUiMessages = {
  loginRequired: string;
  enterBlocked: string;
  broadcastPending: string;
};

const DEFAULT_MESSAGES: LiveUiMessages = {
  loginRequired: '로그인후 이용해주세요',
  enterBlocked: '코인 및 등급 부족하여 입장할수 없습니다',
  broadcastPending: '관리자 승인후 방송가능 합니다.',
};

let cachedMessages: LiveUiMessages | null = null;

export async function getLiveUiMessages(): Promise<LiveUiMessages> {
  if (cachedMessages) return cachedMessages;

  const branding = await fetchSiteBranding();
  const ui = (branding as { ui_messages?: Partial<LiveUiMessages> } | null)?.ui_messages;

  cachedMessages = {
    loginRequired: ui?.loginRequired?.trim() || DEFAULT_MESSAGES.loginRequired,
    enterBlocked: ui?.enterBlocked?.trim() || DEFAULT_MESSAGES.enterBlocked,
    broadcastPending: ui?.broadcastPending?.trim() || DEFAULT_MESSAGES.broadcastPending,
  };

  return cachedMessages;
}

export function handleLoginRequired(messages: LiveUiMessages, redirectPath: string): void {
  if (typeof window !== 'undefined' && isLoginPath(window.location.pathname)) {
    showOneliveAlert(messages.loginRequired);
    return;
  }

  const safeRedirect = sanitizeLoginRedirect(redirectPath, window.location.pathname || '/');
  showOneliveAlert(messages.loginRequired, () => {
    navigate(buildLoginPath(safeRedirect));
  });
}

export function handleLiveRoomClick(
  room: LiveRoom,
  messages: LiveUiMessages,
  redirectPath?: string,
): void {
  void room;
  const user = getCurrentUser();
  const path = redirectPath || (typeof window !== 'undefined' ? window.location.pathname : '/');

  if (!user?.uuid) {
    handleLoginRequired(messages, path);
    return;
  }

  showOneliveAlert(messages.enterBlocked);
}

export function handleBroadcastStart(messages: LiveUiMessages): void {
  const user = getCurrentUser();

  if (!user?.uuid) {
    handleLoginRequired(messages, '/');
    return;
  }

  showOneliveAlert(messages.broadcastPending);
}

export async function guardLiveRoomPageAccess(
  messages: LiveUiMessages,
  fallbackPath = '/live',
): Promise<boolean> {
  const user = getCurrentUser();
  const path = typeof window !== 'undefined' ? window.location.pathname : fallbackPath;

  if (!user?.uuid) {
    handleLoginRequired(messages, path);
    return false;
  }

  showOneliveAlert(messages.enterBlocked, () => {
    navigate(fallbackPath);
  });
  return false;
}
